import { Injectable, Inject, InjectionToken, Optional } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';

/**
 * Injection token for leadership API path
 */
export const LEADERSHIP_API_PATH = new InjectionToken<string>('LEADERSHIP_API_PATH');

/** Base URL for hackathon challenge-evaluator (no trailing slash). Apps can override via DI. */
export const CHALLENGE_EVALUATOR_BASE_URL = new InjectionToken<string>(
  'CHALLENGE_EVALUATOR_BASE_URL',
);

const DEFAULT_CHALLENGE_EVALUATOR_BASE_URL =
  'http://benchmark.tanuh.ai/challenge-evaluator-backend';

/**
 * Interface for leadership member data
 */
export interface LeadershipMember {
  id?: string;
  name: string;
  role: string;
  title?: string;
  rank: number;
  score?: number;
  bio?: string;
  email?: string;
  linkedin?: string;
  avatar?: string;
  category?: 'providers' | 'assets' | 'organisation';
  [key: string]: any;
}

/**
 * Interface for leadership board response
 */
export interface LeadershipBoardResponse {
  members?: LeadershipMember[];
  data?: LeadershipMember[];
  total?: number;
  page?: number;
  size?: number;
  [key: string]: any;
}

export interface HackathonSubmitError extends Error {
  status?: number;
  detail?: string;
  nextAllowedAt?: string;
}

export interface HackathonSubmitResponse {
  _httpStatus: number;
  [key: string]: any;
}

export interface HackathonWorkflowResponse {
  _httpStatus: number;
  [key: string]: any;
}

export interface HackathonRunDetailsResponse {
  _httpStatus: number;
  output?: string;
  [key: string]: any;
}

export interface HackathonRateLimitResponse {
  _httpStatus: number;
  [key: string]: any;
}

/** One row from `my_workflows` (shape varies slightly by problem statement). */
export interface HackathonMyWorkflowRow {
  id?: string;
  databank_id?: string;
  preferred_username?: string;
  challenge_name?: string;
  problem_statement?: string;
  workflow_name?: string;
  status?: string;
  score?: number;
  max_score?: number;
  passed_tests?: number;
  failed_tests?: number;
  total_tests?: number;
  error_message?: string;
  created_at?: string;
  started_at?: string;
  finished_at?: string;
  [key: string]: any;
}

/**
 * Normalizes `my_workflows` JSON (top-level array or wrapped lists) into rows.
 */
export function extractHackathonMyWorkflowsListFromResponse(
  response: HackathonWorkflowResponse | null | undefined,
): HackathonMyWorkflowRow[] {
  if (!response) {
    return [];
  }
  const listKeys = ['items', 'data', 'workflows', 'results', 'result'] as const;
  for (const key of listKeys) {
    const value = response[key];
    if (Array.isArray(value)) {
      return value as HackathonMyWorkflowRow[];
    }
  }
  return [];
}

/**
 * Leaderboard API Service for handling Leaderboard-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class LeadershipApiService {
  private readonly hackathonLeaderboardUrl: string;
  private readonly hackathonSubmitUrl: string;
  private readonly hackathonMyWorkflowsUrl: string;
  private readonly hackathonRunsUrl: string;
  private readonly hackathonRateLimitBaseUrl: string;

  constructor(
    private networkService: NetworkService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(LEADERSHIP_API_PATH) private leadershipApiPath: string,
    @Optional()
    @Inject(CHALLENGE_EVALUATOR_BASE_URL)
    challengeEvaluatorBaseUrl: string | null,
  ) {
    const base = challengeEvaluatorBaseUrl ?? DEFAULT_CHALLENGE_EVALUATOR_BASE_URL;
    this.hackathonLeaderboardUrl = `${base}/leaderboards`;
    this.hackathonSubmitUrl = `${base}/submit`;
    this.hackathonMyWorkflowsUrl = `${base}/my_workflows`;
    this.hackathonRunsUrl = `${base}/runs`;
    this.hackathonRateLimitBaseUrl = `${base}/rate_limit`;
  }

  /**
   * Get leadership board for providers
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with leadership board data for providers
   */
  async getLeadershipForProvider(
    page: number = 1,
    size: number = 10,
  ): Promise<LeadershipBoardResponse> {
    try {
      const url = `${this.basePathUrl}${this.leadershipApiPath}provider?page=${page}&size=${size}`;
      console.log('LeadershipApiService: Calling Provider API with URL:', url);

      const response = await this.networkService.get_api(url, undefined, false);

      console.log(
        'LeadershipApiService: Provider API response received:',
        response,
      );
      return response;
    } catch (error) {
      console.error('Error fetching leadership for providers:', error);
      throw error;
    }
  }

  /**
   * Get leaderboard for assets
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with leaderboard data for assets
   */
  async getLeaderboardForAssets(
    page: number = 1,
    size: number = 10,
  ): Promise<LeadershipBoardResponse> {
    try {
      const url = `${this.basePathUrl}${this.leadershipApiPath}asset?page=${page}&size=${size}`;
      console.log('LeadershipApiService: Calling Assets API with URL:', url);

      const response = await this.networkService.get_api(url, undefined, false);

      console.log(
        'LeadershipApiService: Assets API response received:',
        response,
      );
      return response;
    } catch (error) {
      console.error('Error fetching leaderboard for assets:', error);
      throw error;
    }
  }

  /**
   * Get leaderboard for organisations
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with leaderboard data for organisations
   */
  async getLeaderboardForOrganisations(
    page: number = 1,
    size: number = 10,
  ): Promise<LeadershipBoardResponse> {
    try {
      const url = `${this.basePathUrl}${this.leadershipApiPath}organization?page=${page}&size=${size}`;
      console.log(
        'LeadershipApiService: Calling Organisation API with URL:',
        url,
      );

      const response = await this.networkService.get_api(url, undefined, false);

      console.log(
        'LeadershipApiService: Organisation API response received:',
        response,
      );
      return response;
    } catch (error) {
      console.error('Error fetching leaderboard for organisations:', error);
      throw error;
    }
  }

  /**
   * Get hackathon leaderboard for a specific problem statement track.
   * @param pvc Problem statement PVC value
   * @returns Promise with leaderboard data
   */
  async getHackathonLeaderboard(
    pvc: string,
    accessToken: string,
  ): Promise<LeadershipBoardResponse> {
    try {
      const body = { pvc };
      console.log(
        'LeadershipApiService: Calling Hackathon leaderboard API with payload:',
        body,
      );

      const response = await this.networkService.post_api(
        this.hackathonLeaderboardUrl,
        body,
        {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        false,
      );

      console.log(
        'LeadershipApiService: Hackathon leaderboard API response received:',
        response,
      );
      return response;
    } catch (error) {
      console.error('Error fetching hackathon leaderboard:', error);
      throw error;
    }
  }

  /**
   * Submit hackathon evaluation for a challenge/problem statement.
   * @param challengeName Human-readable challenge name
   * @param pvc Problem statement PVC value
   * @returns Promise with submit API response
   */
  async submitHackathonEvaluation(
    challengeName: string,
    pvc: string,
    accessToken: string,
  ): Promise<HackathonSubmitResponse> {
    try {
      const body = {
        challenge_name: challengeName,
        pvc,
      };
      console.log(
        'LeadershipApiService: Calling Hackathon submit API with payload:',
        body,
      );
      // Use Fetch API directly to avoid Angular HTTP interceptor header overrides.
      // For this endpoint we must send the exact Authorization token from Postman.
      const response = await fetch(this.hackathonSubmitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      const responseText = await response.text();
      let responseBody: any = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail = responseBody?.detail || responseText || 'Unknown error';
        const error = new Error(
          `Hackathon submit failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = detail;

        const nextAllowedAtMatch = String(detail).match(
          /next allowed at\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)/i,
        );
        if (nextAllowedAtMatch?.[1]) {
          error.nextAllowedAt = nextAllowedAtMatch[1];
        }

        throw error;
      }

      const normalizedResponse =
        responseBody && typeof responseBody === 'object'
          ? responseBody
          : { value: responseBody };
      const result: HackathonSubmitResponse = {
        ...normalizedResponse,
        _httpStatus: response.status,
      };
      console.log(
        'LeadershipApiService: Hackathon submit API response received:',
        result,
      );
      return result;
    } catch (error) {
      console.error('Error submitting hackathon evaluation:', error);
      throw error;
    }
  }

  async getHackathonMyWorkflows(
    pvc: string,
    accessToken: string,
  ): Promise<HackathonWorkflowResponse> {
    try {
      const body = { pvc };
      console.log(
        'LeadershipApiService: Calling Hackathon my_workflows API with payload:',
        body,
      );

      const response = await fetch(this.hackathonMyWorkflowsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      const responseText = await response.text();
      let responseBody: any = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail = responseBody?.detail || responseText || 'Unknown error';
        const error = new Error(
          `Hackathon my_workflows failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = detail;
        throw error;
      }

      let payloadForMerge: Record<string, unknown>;
      if (Array.isArray(responseBody)) {
        payloadForMerge = { items: responseBody };
      } else if (responseBody && typeof responseBody === 'object') {
        payloadForMerge = responseBody as Record<string, unknown>;
      } else {
        payloadForMerge = { value: responseBody };
      }
      const result: HackathonWorkflowResponse = {
        ...(payloadForMerge as object),
        _httpStatus: response.status,
      };
      console.log(
        'LeadershipApiService: Hackathon my_workflows API response received:',
        result,
      );
      return result;
    } catch (error) {
      console.error('Error fetching hackathon my_workflows:', error);
      throw error;
    }
  }

  async getHackathonRunDetails(
    runId: string,
    accessToken: string,
  ): Promise<HackathonRunDetailsResponse> {
    try {
      const response = await fetch(
        `${this.hackathonRunsUrl}/${encodeURIComponent(runId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const responseText = await response.text();
      let responseBody: any = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail =
          responseBody?.detail ||
          responseBody?.error ||
          responseText ||
          'Unknown error';
        const error = new Error(
          `Hackathon run details failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = detail;
        throw error;
      }

      const normalizedResponse =
        responseBody && typeof responseBody === 'object'
          ? responseBody
          : { value: responseBody };
      return {
        ...normalizedResponse,
        _httpStatus: response.status,
      };
    } catch (error) {
      console.error('Error fetching hackathon run details:', error);
      throw error;
    }
  }

  async getHackathonRateLimit(
    problemStatementKey: string,
    accessToken: string,
  ): Promise<HackathonRateLimitResponse> {
    try {
      const response = await fetch(
        `${this.hackathonRateLimitBaseUrl}/${encodeURIComponent(problemStatementKey)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const responseText = await response.text();
      let responseBody: any = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail =
          responseBody?.detail ||
          responseBody?.error ||
          responseText ||
          'Unknown error';
        const error = new Error(
          `Hackathon rate_limit failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = detail;
        throw error;
      }

      const normalizedResponse =
        responseBody && typeof responseBody === 'object'
          ? responseBody
          : { value: responseBody };
      return {
        ...normalizedResponse,
        _httpStatus: response.status,
      };
    } catch (error) {
      console.error('Error fetching hackathon rate_limit:', error);
      throw error;
    }
  }
}
